import { Box } from '@chakra-ui/react';
import { useMultiStyleConfig } from '@chakra-ui/system';
import { mergeWith } from '@chakra-ui/utils';
import React, { useState, useEffect } from 'react';
import { ReactDatePickerCustomHeaderProps } from 'react-datepicker';
import { createPortal } from 'react-dom';

import DatePickerFooter from './_DatePickerFooter';

import IconButton from 'atoms/Button/IconButton';
import Select from 'atoms/Select/Select';

const months = [
  'January',
  'February',
  'March',
  'April',
  'May',
  'June',
  'July',
  'August',
  'September',
  'October',
  'November',
  'December'
];

type DatePickerHeaderProps = ReactDatePickerCustomHeaderProps & {
  disableMonthYearSelector: boolean;
  focusTodayButton: () => void;
  yearSelectOptions: number[];
  footerRef: React.RefObject<HTMLDivElement>;
};

const DatePickerHeader = (props: DatePickerHeaderProps): React.ReactElement => {
  const {
    date,
    decreaseMonth,
    increaseMonth,
    changeMonth,
    changeYear,
    monthDate,
    disableMonthYearSelector,
    focusTodayButton,
    yearSelectOptions,
    footerRef
  } = props;
  const [didMount, setDidMount] = useState(false);
  const DatePickerTheme = useMultiStyleConfig('DatePickerTheme', props);
  const SelectTheme = useMultiStyleConfig('Select', { variant: 'ghost', size: 'sm' });

  useEffect(() => {
    setDidMount(true);
  }, []);

  return (
    <Box display="flex" height="32px" justifyContent="space-between" marginBottom="8px" p="6px 0 8px 0">
      <Box alignItems="center" columnGap="16px" display="flex" pl="10px">
        {disableMonthYearSelector ? (
          <>
            <span>{months[date.getMonth()]}</span>
            <span>{date.getFullYear()}</span>
          </>
        ) : (
          <Box display="flex" marginLeft="-12px">
            <Box position="relative">
              {/* fake select text to hug the space and avoid the browser's fixed width in <select> element */}
              <Box opacity="0">
                <Box aria-hidden sx={mergeWith(SelectTheme.field, DatePickerTheme.calendarFakeSelect)}>
                  {months[date.getMonth()]}
                </Box>
              </Box>
              <Box position="absolute" top="0">
                <Select
                  aria-label="Select month"
                  onChange={({ target: { value } }) => changeMonth(months.indexOf(value))}
                  size="sm"
                  sx={DatePickerTheme.calendarSelect}
                  value={months[date.getMonth()]}
                  variant="ghost"
                >
                  {months.map(month => (
                    <option key={month}>{month}</option>
                  ))}
                </Select>
              </Box>
            </Box>
            <Box>
              <Select
                aria-label="Select year"
                onChange={({ target: { value } }) => changeYear(Number(value))}
                size="sm"
                sx={DatePickerTheme.calendarSelect}
                value={date.getFullYear()}
                variant="ghost"
              >
                {yearSelectOptions.map(year => (
                  <option key={year}>{year}</option>
                ))}
              </Select>
            </Box>
          </Box>
        )}
      </Box>
      <Box alignItems="center" display="flex">
        <IconButton
          aria-label="Previous Month"
          onClick={event => {
            event.stopPropagation();
            decreaseMonth();
          }}
          size="sm"
          sx={DatePickerTheme.calendarPreviousMonthButton}
          type="Chevron"
          variant="ghost"
        />
        <IconButton
          aria-label="Next Month"
          className="next-month"
          onClick={increaseMonth}
          size="sm"
          sx={DatePickerTheme.calendarNextMonthButton}
          type="Chevron"
          variant="ghost"
        />
      </Box>
      {/* render footer with APIs from the datepicker header */}
      {didMount &&
        footerRef?.current &&
        createPortal(
          <DatePickerFooter
            changeMonth={changeMonth}
            changeYear={changeYear}
            focusTodayButton={focusTodayButton}
            monthDate={monthDate}
          />,
          footerRef?.current
        )}
    </Box>
  );
};

export default DatePickerHeader;
